import React from 'react';
import { CheckCircle2, CreditCard, Loader2, RefreshCw, Unplug } from 'lucide-react';

import { useAuth } from '@/shared/auth';
import {
  connectTenant,
  disconnectTenant,
  getAuthStatus,
  seedWorkflowCatalog,
  type AuthStatus,
} from '@/features/system/developer/components/simulations/mercadopago/api/mercadopagoApi';

function resolveDefaultTenantKey(user: any): string {
  return String(user?.username || user?.tenant_id || 'dev').trim();
}

export default function MercadoPagoAccountManagement() {
  const { user, loading: authLoading } = useAuth();
  const defaultTenantKey = React.useMemo(() => resolveDefaultTenantKey(user), [user]);

  const [tenantKey, setTenantKey] = React.useState(defaultTenantKey);
  const [status, setStatus] = React.useState<AuthStatus | null>(null);
  const [statusLoading, setStatusLoading] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState<'connect' | 'disconnect' | 'seed' | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);

  React.useEffect(() => {
    setTenantKey((current) => current || defaultTenantKey);
  }, [defaultTenantKey]);

  const loadStatus = React.useCallback(async () => {
    const nextTenantKey = tenantKey.trim();
    if (!nextTenantKey) {
      setStatus(null);
      return;
    }
    setStatusLoading(true);
    setFeedback(null);
    try {
      const nextStatus = await getAuthStatus(nextTenantKey);
      setStatus(nextStatus);
    } catch (err: any) {
      setFeedback(err?.message || 'No se pudo consultar el estado de Mercado Pago.');
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, [tenantKey]);

  React.useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const handleConnect = React.useCallback(async () => {
    const nextTenantKey = tenantKey.trim();
    if (!nextTenantKey) return;
    setActionLoading('connect');
    setFeedback(null);
    try {
      const result = await connectTenant(nextTenantKey);
      setFeedback(result.message || 'Cuenta Mercado Pago conectada.');
      await loadStatus();
    } catch (err: any) {
      setFeedback(err?.message || 'No se pudo conectar la cuenta Mercado Pago.');
    } finally {
      setActionLoading(null);
    }
  }, [loadStatus, tenantKey]);

  const handleDisconnect = React.useCallback(async () => {
    const nextTenantKey = tenantKey.trim();
    if (!nextTenantKey) return;
    setActionLoading('disconnect');
    setFeedback(null);
    try {
      const result = await disconnectTenant(nextTenantKey);
      setFeedback(result.message || 'Cuenta Mercado Pago desconectada.');
      await loadStatus();
    } catch (err: any) {
      setFeedback(err?.message || 'No se pudo desconectar la cuenta Mercado Pago.');
    } finally {
      setActionLoading(null);
    }
  }, [loadStatus, tenantKey]);

  const handleSeed = React.useCallback(async () => {
    setActionLoading('seed');
    setFeedback(null);
    try {
      const result = await seedWorkflowCatalog();
      setFeedback(result.message || (result.already_registered ? 'Workflow ya registrado.' : 'Workflow registrado.'));
    } catch (err: any) {
      setFeedback(err?.message || 'No se pudo registrar el workflow de Mercado Pago.');
    } finally {
      setActionLoading(null);
    }
  }, []);

  const connected = status?.connected === true;

  return (
    <section className="pac-glass rounded-lg p-4 space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-neutral-100">Mercado Pago Account Management</h2>
          <p className="text-sm text-neutral-400 mt-1">
            Administra la cuenta MP usada para generar QR y el workflow del pipeline de pagos.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-sky-900/40 bg-sky-950/30 px-3 py-2 text-xs text-sky-300">
          <CreditCard size={14} />
          <span>{tenantKey || 'sin-tenant'}</span>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
        <div className="space-y-4 rounded-xl border border-neutral-800 bg-black/20 p-4">
          <label className="block space-y-2">
            <span className="text-xs font-medium uppercase tracking-wider text-neutral-500">Tenant Key</span>
            <input
              value={tenantKey}
              onChange={(event) => setTenantKey(event.target.value)}
              className="w-full rounded-lg border border-neutral-800 bg-black/30 px-3 py-2 text-sm text-neutral-100 outline-none transition focus:border-sky-600/50"
              placeholder="tenant slug o tenant id"
            />
          </label>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void loadStatus()}
              disabled={statusLoading || authLoading || !tenantKey.trim()}
              className="inline-flex items-center gap-2 rounded-lg border border-neutral-700 px-3 py-2 text-sm text-neutral-200 transition hover:border-neutral-500 disabled:opacity-40"
            >
              {statusLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Refresh Status
            </button>

            <button
              type="button"
              onClick={() => void handleConnect()}
              disabled={actionLoading !== null || !tenantKey.trim()}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-700/50 bg-emerald-950/30 px-3 py-2 text-sm text-emerald-300 transition hover:border-emerald-500 disabled:opacity-40"
            >
              {actionLoading === 'connect' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Connect
            </button>

            <button
              type="button"
              onClick={() => void handleDisconnect()}
              disabled={actionLoading !== null || !tenantKey.trim()}
              className="inline-flex items-center gap-2 rounded-lg border border-red-700/50 bg-red-950/30 px-3 py-2 text-sm text-red-300 transition hover:border-red-500 disabled:opacity-40"
            >
              {actionLoading === 'disconnect' ? <Loader2 size={14} className="animate-spin" /> : <Unplug size={14} />}
              Disconnect
            </button>

            <button
              type="button"
              onClick={() => void handleSeed()}
              disabled={actionLoading !== null}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-700/50 bg-sky-950/30 px-3 py-2 text-sm text-sky-300 transition hover:border-sky-500 disabled:opacity-40"
            >
              {actionLoading === 'seed' ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              Seed Workflow
            </button>
          </div>

          {feedback && (
            <div className="rounded-lg border border-neutral-800 bg-black/20 px-3 py-2 text-sm text-neutral-300">
              {feedback}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-neutral-800 bg-black/20 p-4">
          <div className="flex items-center justify-between gap-3 border-b border-neutral-900 pb-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500">Connection Status</div>
              <div className="mt-1 text-lg font-medium text-neutral-100">
                {statusLoading ? 'Checking…' : connected ? 'Connected' : 'Not Connected'}
              </div>
            </div>
            <div className={`rounded-full px-3 py-1 text-xs font-medium ${
              connected
                ? 'bg-emerald-950/40 text-emerald-300 border border-emerald-800/40'
                : status?.service_unavailable
                  ? 'bg-amber-950/40 text-amber-300 border border-amber-800/40'
                  : 'bg-neutral-900 text-neutral-400 border border-neutral-800'
            }`}>
              {status?.service_unavailable ? 'Dev Mode' : connected ? 'Ready for QR' : 'Needs Setup'}
            </div>
          </div>

          <dl className="mt-4 grid gap-3 sm:grid-cols-2">
            <StatusRow label="Tenant Key" value={tenantKey || '—'} />
            <StatusRow label="MP User" value={status?.mp_user_id != null ? String(status.mp_user_id) : '—'} />
            <StatusRow label="Store" value={status?.store_id || '—'} />
            <StatusRow label="POS" value={status?.pos_id || '—'} />
            <StatusRow label="Token Expiry" value={status?.token_expires_at || '—'} />
            <StatusRow label="Orchestrator" value={status?.orchestrator_ready ? 'reachable' : status?.service_unavailable ? 'offline' : 'unknown'} />
          </dl>

          <div className="mt-4 rounded-lg border border-neutral-900 bg-black/20 px-3 py-3 text-sm text-neutral-400">
            {authLoading
              ? 'Cargando identidad del usuario…'
              : status?.message || 'Usá Connect para persistir credenciales y habilitar QR.'}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatusRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-neutral-900 bg-black/20 px-3 py-2">
      <dt className="text-xs uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd className="mt-1 break-all text-sm text-neutral-200">{value}</dd>
    </div>
  );
}